import { Badge } from '@shared/ui/Badge';
import { IconScales, IconAlertTriangle } from '@shared/ui/icons';
import { formatMoney } from '../financierFormat';
import type { Liquidity } from './liquidity';
import {
  ThermometerCard,
  ThermometerHeader,
  ThermometerTitle,
  ThermometerStats,
  StatItem,
  StatLabel,
  StatVal,
  LiquidityErrorText,
  RetryMiniButton,
  ProgressTrack,
  Segment,
  DeficitBanner,
  ScaleLegend,
  ScaleLegendItem,
  ScaleSwatch,
  DeficitMark,
} from './styled';

interface CashThermometerProps {
  /** `null` — расчёта нет: не пришёл пульт или список заявок. */
  liquidity: Liquidity | null;
  isDashboardLoading: boolean;
  isDashboardError: boolean;
  availableCash: number | null;
  /** Не оплачено по утверждённым заявкам; `null` — список заявок не загрузился. */
  remainingToDebit: number | null;
  /** Оплачено по выписке; `null` — список заявок не загрузился. */
  totalPaidInView: number | null;
  isRequestsError: boolean;
  onRefetchDashboard: () => void;
  onRetryRequests: () => void;
}

const DEBIT_COLOR = '#0284c7';
const REST_COLOR = '#10b981';

export function CashThermometer({
  liquidity,
  isDashboardLoading,
  isDashboardError,
  availableCash,
  remainingToDebit,
  totalPaidInView,
  isRequestsError,
  onRefetchDashboard,
  onRetryRequests,
}: CashThermometerProps) {
  return (
    <ThermometerCard>
      <ThermometerHeader>
        <ThermometerTitle>
          <IconScales size={18} />
          <span>Балансировщик ликвидности расчётных счетов</span>
          {liquidity ? (
            <Badge
              $tone={
                liquidity.coveragePct >= 100
                  ? 'success'
                  : liquidity.coveragePct >= 70
                    ? 'warning'
                    : 'danger'
              }
            >
              Покрытие: {Math.round(liquidity.coveragePct)}%
            </Badge>
          ) : isDashboardError ? (
            <Badge $tone="danger">Ошибка ликвидности</Badge>
          ) : isRequestsError ? (
            <Badge $tone="danger">Расчёт недоступен</Badge>
          ) : (
            <Badge $tone="neutral">Загрузка…</Badge>
          )}
        </ThermometerTitle>

        <ThermometerStats>
          <StatItem>
            <StatLabel>Доступный кэш:</StatLabel>
            <StatVal $tone={availableCash !== null ? '#15803d' : undefined}>
              {isDashboardLoading ? (
                'Загрузка данных ликвидности…'
              ) : isDashboardError ? (
                <LiquidityErrorText>
                  Ошибка загрузки
                  <RetryMiniButton
                    $size="sm"
                    $variant="ghost"
                    onClick={onRefetchDashboard}
                  >
                    Повторить
                  </RetryMiniButton>
                </LiquidityErrorText>
              ) : (
                formatMoney(availableCash ?? 0)
              )}
            </StatVal>
          </StatItem>
          <StatItem>
            <StatLabel>Остаток к списанию (не оплачено):</StatLabel>
            <StatVal>{remainingToDebit === null ? '—' : formatMoney(remainingToDebit)}</StatVal>
          </StatItem>
          <StatItem>
            <StatLabel>Оплачено по выписке:</StatLabel>
            <StatVal $tone="#0369a1">
              {totalPaidInView === null ? '—' : formatMoney(totalPaidInView)}
            </StatVal>
          </StatItem>
          <StatItem>
            <StatLabel>Прогноз после реестра:</StatLabel>
            <StatVal $tone={liquidity && liquidity.deficit > 0 ? '#b91c1c' : '#15803d'}>
              {liquidity ? formatMoney(liquidity.projectedCash) : '—'}
            </StatVal>
          </StatItem>
        </ThermometerStats>
      </ThermometerHeader>

      {isRequestsError && (
        <LiquidityErrorText role="alert">
          Расчёт недоступен: не загрузился список заявок.
          <RetryMiniButton $size="sm" $variant="ghost" onClick={onRetryRequests}>
            Повторить
          </RetryMiniButton>
        </LiquidityErrorText>
      )}

      {/* Шкала — деньги на счетах: сколько уйдёт по утверждённым заявкам и сколько останется.
          Оплаченное раньше в неё не входит — это число выше. */}
      {liquidity && (
        <>
          <ProgressTrack
            role="img"
            aria-label={`Из доступных денег к списанию ${Math.round(liquidity.debitPct)}%, остаётся ${Math.round(liquidity.restPct)}%`}
          >
            <Segment
              $width={liquidity.debitPct}
              $color={DEBIT_COLOR}
              title={`К списанию по утверждённым заявкам: ${formatMoney(Math.min(remainingToDebit ?? 0, Math.max(0, availableCash ?? 0)))}`}
            />
            <Segment
              $width={liquidity.restPct}
              $color={REST_COLOR}
              title={`Останется после списаний: ${formatMoney(Math.max(0, liquidity.projectedCash))}`}
            />
          </ProgressTrack>
          <ScaleLegend>
            <ScaleLegendItem>
              <ScaleSwatch $color={DEBIT_COLOR} />
              К списанию — {Math.round(liquidity.debitPct)}%
            </ScaleLegendItem>
            <ScaleLegendItem>
              <ScaleSwatch $color={REST_COLOR} />
              Останется — {Math.round(liquidity.restPct)}%
            </ScaleLegendItem>
            {liquidity.deficit > 0 && (
              <DeficitMark>Не хватает {formatMoney(liquidity.deficit)}</DeficitMark>
            )}
          </ScaleLegend>
        </>
      )}

      {liquidity && liquidity.deficit > 0 && (
        <DeficitBanner>
          <IconAlertTriangle size={18} />
          <span>
            <b>Кассовый разрыв {formatMoney(liquidity.deficit)}!</b> Сумма утверждённых платежей превышает доступный остаток на расчётных счетах. Рекомендуется уменьшить суммы (30%/50%) по низкоприоритетным заявкам — остаток автоматически перенесётся в следующий реестр.
          </span>
        </DeficitBanner>
      )}
    </ThermometerCard>
  );
};
